import type { TipTapDoc, TipTapNode } from "../schema/tiptapDoc";

/**
 * Every placeholder token the resolver understands. Adding a token here
 * forces `buildReplacements` to produce it and
 * `TEMPLATE_PLACEHOLDER_PREVIEW_VALUES` to provide a preview value — TSC
 * fails on either omission.
 */
export type TemplatePlaceholderToken =
    | "{{current_month}}"
    | "{{current_year}}"
    | "{{last_month}}"
    | "{{last_month_year}}"
    | "{{last_year}}"
    | "{{participant_names}}"
    | "{{first_participant_first_name}}"
    | "{{reply_to_name}}"
    | "{{my_first_name}}"
    | "{{my_last_name}}"
    | "{{my_full_name}}"
    | "{{organization_name}}";

export interface TemplateValues {
    /** Defaults to the current date. */
    date?: Date;
    /** Names of the known participants — recipients or thread participants. */
    participantNames?: string[];
    /** Name of the person being replied to / last poster in the thread. */
    replyToName?: string;
    /** First name of the current user. */
    userFirstName?: string;
    /** Last name of the current user. */
    userLastName?: string;
    /** Organization name of the first non-Compass thread participant —
     *  resolves {{organization_name}}. */
    organizationName?: string;
}

/**
 * Sample values for each placeholder, used to render a template preview in
 * the admin panel. Typed as a complete `Record` so adding a new token to
 * `TemplatePlaceholderToken` forces a preview value here — TSC fails
 * otherwise. The set is internally consistent (as if "today" were May 2026).
 */
export const TEMPLATE_PLACEHOLDER_PREVIEW_VALUES: Record<TemplatePlaceholderToken, string> = {
    "{{current_month}}": "May",
    "{{current_year}}": "2026",
    "{{last_month}}": "April",
    "{{last_month_year}}": "2026",
    "{{last_year}}": "2025",
    "{{participant_names}}": "Jordan Lee and Taylor Kim",
    "{{first_participant_first_name}}": "Jordan",
    "{{reply_to_name}}": "Taylor Kim",
    "{{my_first_name}}": "Morgan",
    "{{my_last_name}}": "Reed",
    "{{my_full_name}}": "Morgan Reed",
    "{{organization_name}}": "Acme Holdings",
};

/** "Alice" / "Alice and Bob" / "Alice, Bob and Carol" */
function joinNames(names: string[]): string {
    const clean = names.map((n) => n.trim()).filter(Boolean);
    if (clean.length === 0) return "";
    if (clean.length === 1) return clean[0];
    return `${clean.slice(0, -1).join(", ")} and ${clean[clean.length - 1]}`;
}

function monthName(date: Date): string {
    return date.toLocaleString("en-US", { month: "long" });
}

function buildReplacements(values: TemplateValues): Record<TemplatePlaceholderToken, string> {
    const date = values.date ?? new Date();
    // Day-of-month 1 avoids end-of-month rollover (e.g. Mar 31 → "Mar 3").
    const lastMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);

    const participants = (values.participantNames ?? []).map((n) => n.trim()).filter(Boolean);
    const firstParticipantFirstName = participants[0]?.split(/\s+/)[0] ?? "";

    const userFirst = (values.userFirstName ?? "").trim();
    const userLast = (values.userLastName ?? "").trim();

    return {
        "{{current_month}}": monthName(date),
        "{{current_year}}": String(date.getFullYear()),
        "{{last_month}}": monthName(lastMonth),
        "{{last_month_year}}": String(lastMonth.getFullYear()),
        "{{last_year}}": String(date.getFullYear() - 1),
        "{{participant_names}}": joinNames(participants),
        "{{first_participant_first_name}}": firstParticipantFirstName,
        "{{reply_to_name}}": (values.replyToName ?? "").trim(),
        "{{my_first_name}}": userFirst,
        "{{my_last_name}}": userLast,
        "{{my_full_name}}": `${userFirst} ${userLast}`.trim(),
        "{{organization_name}}": (values.organizationName ?? "").trim(),
    };
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function applyReplacements(text: string, replacements: Record<string, string>): string {
    let result = text;
    for (const [token, value] of Object.entries(replacements)) {
        // Match the token whitespace-agnostically: "{{name}}", "{{ name }}",
        // "{{name }}" all resolve. `token` is "{{name}}" — strip the braces
        // and rebuild a tolerant pattern around the inner name.
        const inner = token.slice(2, -2).trim();
        const pattern = new RegExp(`\\{\\{\\s*${escapeRegExp(inner)}\\s*\\}\\}`, "g");
        // Function replacer so `$` in the value isn't treated as a special
        // replacement pattern.
        result = result.replace(pattern, () => value);
    }
    return result;
}

function transformNode(node: TipTapNode, replacements: Record<string, string>): TipTapNode | null {
    if (node.type === "text") {
        const text = applyReplacements(node.text, replacements);
        // A text node with empty text is invalid in the TipTap schema; drop it.
        if (text.length === 0) return null;
        return { ...node, text };
    }

    if ("content" in node && Array.isArray(node.content)) {
        const content = node.content
            .map((child) => transformNode(child, replacements))
            .filter((child): child is TipTapNode => child !== null);
        return { ...node, content };
    }

    return node;
}

function applyReplacementsToDoc(doc: TipTapDoc, replacements: Record<string, string>): TipTapDoc {
    const content = doc.content
        .map((child) => transformNode(child, replacements))
        .filter((child): child is TipTapNode => child !== null);
    return { type: "doc", content };
}

/**
 * Resolve template placeholder tokens inside a TipTap document at injection
 * time. Returns a new document; the input is not mutated.
 *
 * Supported tokens: see {@link TemplatePlaceholderToken}.
 *
 * Replacement is per-text-node, so a token split across separately-marked
 * text nodes will not match — admins should type tokens as plain text.
 */
export function resolveTemplatePlaceholders(
    doc: TipTapDoc,
    values: TemplateValues = {},
): TipTapDoc {
    return applyReplacementsToDoc(doc, buildReplacements(values));
}

/**
 * Resolve a document using {@link TEMPLATE_PLACEHOLDER_PREVIEW_VALUES} —
 * used by the admin panel to show what a template will look like once its
 * placeholders are filled in.
 */
export function resolveTemplatePreview(doc: TipTapDoc): TipTapDoc {
    return applyReplacementsToDoc(doc, TEMPLATE_PLACEHOLDER_PREVIEW_VALUES);
}

/**
 * Plain-string sibling of {@link resolveTemplatePlaceholders} — used for
 * subject templates that have no rich text.
 */
export function resolveTemplateSubject(text: string, values: TemplateValues = {}): string {
    return applyReplacements(text, buildReplacements(values));
}

/**
 * Plain-string sibling of {@link resolveTemplatePreview} — fills a subject
 * template with the sample preview values for the admin preview panel.
 */
export function resolveTemplateSubjectPreview(text: string): string {
    return applyReplacements(text, TEMPLATE_PLACEHOLDER_PREVIEW_VALUES);
}
